
import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { toast } from '@/hooks/use-toast';

type AuthGuardProps = {
  children: ReactNode;
  requireAuth?: boolean; // If true, redirects to login if not authenticated
  requireUnauth?: boolean; // If true, redirects away if authenticated
  requireAuthor?: boolean; // If true, redirects to become-author if not an author
  requireAdmin?: boolean; // If true, redirects to home if not an admin
  authorOnly?: boolean; // If true, only allows access in author view
  readerOnly?: boolean; // If true, only allows access in reader view
};

const AuthGuard = ({ 
  children, 
  requireAuth = false,
  requireUnauth = false,
  requireAuthor = false,
  requireAdmin = false,
  authorOnly = false,
  readerOnly = false
}: AuthGuardProps) => {
  const { user, isLoading, isAuthor, isAdmin, currentView, switchToReaderView, switchToAuthorView } = useAuth();
  const location = useLocation();

  // Wait for auth to be initialized
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-chapterly-purple">Loading...</div>
      </div>
    );
  }

  // If auth is required and user is not logged in
  if (requireAuth && !user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname)}`} />;
  }

  // If unauth is required (like login page) and user is logged in
  if (requireUnauth && user) {
    const redirectTo = new URLSearchParams(location.search).get('redirect') || '/';
    return <Navigate to={redirectTo} />;
  }
  
  // If author role is required and user is not an author
  if (requireAuthor && !isAuthor && user) {
    return <Navigate to="/become-author" />;
  }
  
  // If admin role is required and user is not an admin
  if (requireAdmin && !isAdmin && user) {
    // Use setTimeout to prevent "Cannot update a component while rendering a different component" error
    setTimeout(() => {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: "You need administrator privileges to access this page."
      });
    }, 0);
    
    return <Navigate to="/" />;
  }
  
  // View-specific restrictions for authors who can switch between views
  if (authorOnly && isAuthor && currentView !== 'author') {
    // Auto-switch to author view for author-only pages
    switchToAuthorView();
  }
  
  if (readerOnly && isAuthor && currentView !== 'reader') {
    // Auto-switch to reader view for reader-only pages
    switchToReaderView();
  }

  return <>{children}</>;
};

export default AuthGuard;
